(function () {
  'use strict';

  /**
   * Swym wishlist page customizations
   * - Replace "Add to Cart" with a "View Details" link
   * - Inject SKU next to the price/variant
   * - Remove "Default Title" noise
   * - Show/hide the variant selector block
   *
   * Injection is driven by a single rAF-debounced pass guarded by a
   * running/pending lock, plus a per-product fetch cache and idempotent
   * DOM writes. Once the DOM is settled a pass makes zero mutations, so
   * the observer stops re-firing itself (no fetch storm / loop).
   */

  var PROCESSED = 'swymConverted';
  var productCache = new Map(); // productUrl(no query) -> Promise<product|null>
  var wsPriceCache = new Map(); // productUrl(no query) -> Promise<{variantId: cents}>

  // Swym shows the variant's own (retail) price. Wholesale prices live in the
  // variant metafield custom.wholesale_price, exposed to wholesale customers only
  // by templates/product.ws-price.liquid (everyone else gets {}).
  var isLoggedIn = !!(
    (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.page &&
      ShopifyAnalytics.meta.page.customerId) ||
    (window.Shopify && window.Shopify.customer === true)
  );

  var rafId = null;
  var running = false;
  var pending = false;

  // ---- fetch (cached, one in-flight promise per product) ------------------

  function fetchProduct(productUrl) {
    var key = productUrl.split('?')[0];

    if (productCache.has(key)) return productCache.get(key);

    var p = fetch(key + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        console.warn('[swym-wishlist] product fetch failed:', key, err);
        productCache.delete(key); // allow a retry on a later pass
        return null;
      });

    productCache.set(key, p);
    return p;
  }

  function fetchWsPrices(productUrl) {
    var key = productUrl.split('?')[0];

    if (wsPriceCache.has(key)) return wsPriceCache.get(key);

    var p = fetch(key + '?view=ws-price', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (text) {
        // Not JSON (e.g. template missing -> normal product page): cache {} so
        // later passes don't refetch.
        try {
          var data = JSON.parse(text.trim());
          return data && typeof data === 'object' ? data : {};
        } catch (e) {
          return {};
        }
      })
      .catch(function (err) {
        console.warn('[swym-wishlist] wholesale price fetch failed:', key, err);
        wsPriceCache.delete(key);
        return {};
      });

    wsPriceCache.set(key, p);
    return p;
  }

  function formatMoney(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(cents / 100);
  }

  // ---- cheap, idempotent per-item DOM ops ---------------------------------

  function replaceButton(item) {
    var btn = item.querySelector(
      '.swym-storefront-layout-grid-item-add-to-cart-button'
    );
    if (!btn || btn.tagName === 'A' || btn.dataset[PROCESSED]) return;

    var productUrl = item.getAttribute('data-du');
    if (!productUrl) return;

    var link = document.createElement('a');
    link.href = productUrl;
    link.textContent = 'View Details';
    link.className =
      'swym-storefront-layout-grid-item-add-to-cart-button swym-view-details-btn';
    link.dataset[PROCESSED] = 'true';

    btn.replaceWith(link);
  }

  function removeDefaultTitle(item) {
    item
      .querySelectorAll(
        '.swym-storefront-layout-variant, .swym-storefront-layout-variant-devider'
      )
      .forEach(function (el) {
        if (el.textContent.trim() === 'Default Title' || el.textContent.trim() === 'Retail') el.remove();
      });
  }

  function handleVariantVisibility(item) {
    var variantsContainer = item.querySelector(
      '.swym-storefront-layout-grid-item-variants-container'
    );
    if (!variantsContainer) return;

    var priceDivider = item.querySelector(
      '.swym-storefront-layout-price-devider'
    );
    var hasSelector = !!variantsContainer.querySelector(
      '[id^="variant-selector-"]'
    );

    variantsContainer.style.display = hasSelector ? '' : 'none';
    if (priceDivider) priceDivider.style.display = hasSelector ? '' : 'none';
  }

  function injectSku(item, sku) {
    var priceVariant = item.querySelector(
      '.swym-storefront-layout-grid-item-price-variant'
    );
    if (!priceVariant) return;

    var existing = priceVariant.querySelector('.custom-sku');
    if (existing && existing.dataset.sku === sku) return; // already correct -> no mutation
    if (existing) existing.remove();

    var skuSpan = document.createElement('span');
    skuSpan.className = 'custom-sku';
    skuSpan.dataset.sku = sku;
    skuSpan.textContent = ' | SKU: ' + sku;
    priceVariant.appendChild(skuSpan);
  }

  async function applyWholesalePrice(item, variantId, productUrl) {
    if (!isLoggedIn) return;

    var priceEl = item.querySelector('.swym-storefront-layout-grid-item-final-price');
    if (!priceEl) return;

    var prices = await fetchWsPrices(productUrl); // cached after first hit
    var cents = prices[String(variantId)];
    if (!cents) return;

    var text = formatMoney(cents);
    if (priceEl.textContent.trim() !== text) priceEl.textContent = text; // no mutation once settled

    // A retail compare-at price next to the wholesale price would be misleading.
    item
      .querySelectorAll('.swym-storefront-layout-grid-item-price-variant [class*="compare"]')
      .forEach(function (el) {
        if (el.style.display !== 'none') el.style.display = 'none';
      });
  }

  // ---- one pass over all items -------------------------------------------

  async function processItems() {

     // Swym login widget
    updateWishlistLoginButton();

    var items = document.querySelectorAll('swym-storefront-layout-item');

    // Wholesale prices for all items in parallel, so they don't wait behind the
    // sequential SKU fetches below. Runs every pass: Swym may re-render prices.
    if (isLoggedIn) {
      await Promise.all(
        Array.prototype.map.call(items, function (it) {
          var vid = it.getAttribute('data-epi');
          var url = it.getAttribute('data-du');
          return vid && url ? applyWholesalePrice(it, vid, url) : null;
        })
      );
    }

    for (var i = 0; i < items.length; i++) {
      var item = items[i];

      // Cheap idempotent bits run every pass (catch Swym re-renders).
      replaceButton(item);
      removeDefaultTitle(item);
      handleVariantVisibility(item);

      var variantId = item.getAttribute('data-epi');
      var productUrl = item.getAttribute('data-du');
      if (!variantId || !productUrl) continue;

      // Skip the async work if the SKU is already in place.
      var priceVariant = item.querySelector(
        '.swym-storefront-layout-grid-item-price-variant'
      );
      if (priceVariant && priceVariant.querySelector('.custom-sku')) continue;

      var product = await fetchProduct(productUrl); // cached after first hit
      if (!product) continue;

      if (Array.isArray(product.tags) && product.tags.includes('hidden')) {
        item.remove();
        continue;
      }

      var variant = (product.variants || []).find(function (v) {
        return String(v.id) === String(variantId);
      });

      if (variant && variant.sku) injectSku(item, variant.sku);
    }
  }

  // ---- scheduler: rAF debounce + no-overlap lock -------------------------

  async function run() {
    if (running) {
      pending = true; // remember that more work arrived mid-run
      return;
    }
    running = true;
    try {
      await processItems();
    } finally {
      running = false;
      if (pending) {
        pending = false;
        schedule(); // one more pass to cover mutations during the last run
      }
    }
  }

  function schedule() {
    if (rafId) return;

    rafId = requestAnimationFrame(function () {
      rafId = null;

      initCustomShareButton(); // Re-add if Swym re-renders header

      run();
    });
  }

  // ---- boot ---------------------------------------------------------------

  function start() {
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true
    });
    schedule();
  }

   function addShareWishlistButton() {
    const header = document.querySelector('.swym-storefront-layout-header');
    const menuBtn = document.querySelector('.swym-single-share-wishlist-btn');

    if (!header || !menuBtn) return;

    // console.log('menuBtn',menuBtn)

    if (header.querySelector('.custom-share-wishlist-btn')) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'custom-share-wishlist-btn button btn-primary';
    btn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
        <path d="M9.91666 12.8337C9.43055 12.8337 9.01735 12.6635 8.67707 12.3232C8.3368 11.983 8.16666 11.5698 8.16666 11.0837C8.16666 11.0253 8.18124 10.8892 8.21041 10.6753L4.11249 8.28366C3.95693 8.42949 3.77707 8.54373 3.57291 8.62637C3.36874 8.70901 3.14999 8.75033 2.91666 8.75033C2.43055 8.75033 2.01735 8.58019 1.67707 8.23991C1.3368 7.89963 1.16666 7.48644 1.16666 7.00033C1.16666 6.51421 1.3368 6.10102 1.67707 5.76074C2.01735 5.42046 2.43055 5.25033 2.91666 5.25033C3.14999 5.25033 3.36874 5.29164 3.57291 5.37428C3.77707 5.45692 3.95693 5.57116 4.11249 5.71699L8.21041 3.32533C8.19096 3.25727 8.17881 3.19164 8.17395 3.12845C8.16909 3.06526 8.16666 2.99477 8.16666 2.91699C8.16666 2.43088 8.3368 2.01769 8.67707 1.67741C9.01735 1.33713 9.43055 1.16699 9.91666 1.16699C10.4028 1.16699 10.816 1.33713 11.1562 1.67741C11.4965 2.01769 11.6667 2.43088 11.6667 2.91699C11.6667 3.4031 11.4965 3.8163 11.1562 4.15658C10.816 4.49685 10.4028 4.66699 9.91666 4.66699C9.68332 4.66699 9.46457 4.62567 9.26041 4.54303C9.05624 4.46039 8.87638 4.34616 8.72082 4.20033L4.62291 6.59199C4.64235 6.66005 4.6545 6.72567 4.65936 6.78887C4.66423 6.85206 4.66666 6.92255 4.66666 7.00033C4.66666 7.0781 4.66423 7.14859 4.65936 7.21178C4.6545 7.27498 4.64235 7.3406 4.62291 7.40866L8.72082 9.80033C8.87638 9.65449 9.05624 9.54026 9.26041 9.45762C9.46457 9.37498 9.68332 9.33366 9.91666 9.33366C10.4028 9.33366 10.816 9.5038 11.1562 9.84408C11.4965 10.1844 11.6667 10.5975 11.6667 11.0837C11.6667 11.5698 11.4965 11.983 11.1562 12.3232C10.816 12.6635 10.4028 12.8337 9.91666 12.8337Z" fill="currentColor" fill-opacity="0.8"></path>
    </svg>
    <span>Share Wishlist</span>
    `;

    btn.addEventListener('click', function () {

      // If Share button already exists, don't open the 3-dot menu.
      let shareBtn = document.querySelector('.share-collection-button');

      if (shareBtn) {
        shareBtn.click();
        return;
      }

      // Otherwise open the menu once.
      menuBtn.click();

      let attempts = 0;

      const timer = setInterval(function () {

        shareBtn = document.querySelector('.share-collection-button');

        if (shareBtn) {
          clearInterval(timer);

          // Small delay so Swym finishes rendering.
          setTimeout(function () {
            shareBtn.click();
          }, 200);

          return;
        }

        attempts++;

        if (attempts > 20) {
          clearInterval(timer);
        }

      }, 100);

    });

    header.insertBefore(btn, menuBtn);

    // Hide original 3-dot button
    menuBtn.style.display = 'none';
  }

  function initCustomShareButton() {

    // console.log('prod laoadded')
    const timer = setInterval(function () {

      if (
        document.querySelector('.swym-storefront-layout-header') &&
        document.querySelector('.swym-single-share-wishlist-btn')
      ) {
        clearInterval(timer);
        addShareWishlistButton();
      }

    }, 4000);

    setTimeout(function () {
      clearInterval(timer);
    }, 5000);
  }

  function updateWishlistLoginButton() {
      var btn = document.querySelector('.swym-storefront-layout-login-button');

      if (!btn || btn.tagName === 'A') return;

      var link = document.createElement('a');

      link.href = '/account/login?return_url=/pages/swym-wishlist'; // Your legacy login URL
      link.className = `btn btn-primary`;
      link.innerHTML = btn.innerHTML;

      link.style.display = 'inline-flex';
      link.style.alignItems = 'center';
      link.style.justifyContent = 'center';
      link.style.textDecoration = 'none';

      btn.replaceWith(link);
    }



  function swymCallbackFn() {
    start();
    // initCustomShareButton();
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(swymCallbackFn);

  
})();



// Old code------------
// (function () {
//   'use strict';

//   // Replace Add To Cart with View Details
//   function replaceSwymButtons() {

//     document
//       .querySelectorAll('.swym-storefront-layout-grid-item-add-to-cart-button')
//       .forEach(function (btn) {

//         if (btn.dataset.swymConverted) return;

//         var item = btn.closest('swym-storefront-layout-item');

//         if (!item) return;

//         var productUrl = item.getAttribute('data-du');

//         if (!productUrl) return;

//         var link = document.createElement('a');

//         link.href = productUrl;
//         link.innerHTML = 'View Details';

//         link.className =
//           'swym-storefront-layout-grid-item-add-to-cart-button swym-view-details-btn';

//         link.dataset.swymConverted = 'true';

//         btn.replaceWith(link);
//       });
//   }

// // Add SKU
// async function addSkuToWishlistItems() {

//   const items = document.querySelectorAll(
//     'swym-storefront-layout-item'
//   );

//   for (const item of items) {

//     const variantId = item.getAttribute('data-epi');
//     const productUrl = item.getAttribute('data-du');

//     if (!variantId || !productUrl) continue;

//     try {

//       const response = await fetch(productUrl + '.js');
//       const product = await response.json();

//       if (product.tags.includes("hidden")) {
//           item.remove();
//           continue;
//       }

//       const variant = product.variants.find(function (v) {
//         return String(v.id) === String(variantId);
//       });

//       if (!variant || !variant.sku) continue;

//       const priceVariant = item.querySelector(
//         '.swym-storefront-layout-grid-item-price-variant'
//       );

//       if (!priceVariant) continue;

//       // REMOVE OLD SKU IF EXISTS
//       const existingSku =
//         priceVariant.querySelector('.custom-sku');

//       if (existingSku) {
//         existingSku.remove();
//       }

//       // CREATE NEW SKU
//       const skuSpan = document.createElement('span');

//       skuSpan.className = 'custom-sku';
//       skuSpan.textContent = ' | SKU: ' + variant.sku;

//       priceVariant.appendChild(skuSpan);

//     } catch (error) {

//       console.log('SKU Error:', error);

//     }
//   }
// }


// function removeDefaultTitle() {

//   document
//     .querySelectorAll('.swym-storefront-layout-variant, .swym-storefront-layout-variant-devider')
//     .forEach(function (el) {

//       if (el.textContent.trim() === 'Default Title') {
//         el.remove();
//       }

//     });
// }


// function handleVariantVisibility() {

//   document
//     .querySelectorAll('swym-storefront-layout-item')
//     .forEach(function (item) {

//       const variantsContainer = item.querySelector(
//         '.swym-storefront-layout-grid-item-variants-container'
//       );

//       const priceDivider = item.querySelector(
//         '.swym-storefront-layout-price-devider'
//       );

//       if (!variantsContainer) return;

//       // variant-selector check
//       const variantSelector = variantsContainer.querySelector(
//         '[id^="variant-selector-"]'
//       );

//       if (variantSelector) {

//         // SHOW
//         variantsContainer.style.display = '';
        
//         if (priceDivider) {
//           priceDivider.style.display = '';
//         }

//       } else {

//         // HIDE
//         variantsContainer.style.display = 'none';

//         if (priceDivider) {
//           priceDivider.style.display = 'none';
//         }

//       }

//     });
// }
//   // Run immediately
//   function initSwymWishlistCustomizations() {

//     replaceSwymButtons();
//     addSkuToWishlistItems();
//      removeDefaultTitle();
//      handleVariantVisibility();

//   }

//   // Initial run
//   initSwymWishlistCustomizations();

//   // Watch for Swym dynamic rendering
//   const observer = new MutationObserver(function () {

//     initSwymWishlistCustomizations();

//   });

//   observer.observe(document.body, {
//     childList: true,
//     subtree: true
//   });

// })();


